import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { AdapterLuxonSunday } from '../../utils/AdapterSundayLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useUser } from '../../Hooks/useUser';
import axios from '../../api/axios';
import { Settings, DateTime } from 'luxon';
import { useFormData } from "../../Hooks/useFormDataContext";

const endpoint = '/api/users/alluserinfo';

// Passport dates are stored as strings (e.g. "04/16/1990"); the MUI Luxon
// DatePicker needs a Luxon DateTime. Convert strings on the way in, pass through
// DateTime values the user picks, and return null for blank/invalid dates.
const toDT = (v) => {
  if (!v) return null;
  if (DateTime.isDateTime(v)) return v;
  if (v instanceof Date) { const d = DateTime.fromJSDate(v); return d.isValid ? d : null; }
  const s = String(v).trim();
  if (!s) return null;
  let d = DateTime.fromFormat(s, 'MM/dd/yyyy');
  if (!d.isValid) d = DateTime.fromFormat(s, 'MM-dd-yyyy');
  if (!d.isValid) d = DateTime.fromISO(s);
  if (!d.isValid) d = DateTime.fromJSDate(new Date(s));
  return d.isValid ? d : null;
};

const Passport = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: contextUser } = useUser();
  const { updateForm } = useFormData();

  const isControlled = Object.prototype.hasOwnProperty.call(props, 'User');

  const [User, setUser] = useState(props.User ?? {});
  const [UserSettings, setUserSettings] = useState(props.UserSettings ?? {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isControlled || !props.User) return;
    setUser(props.User);
  }, [isControlled, props.User]);

  useEffect(() => {
    if (!isControlled || !props.UserSettings) return;
    setUserSettings(props.UserSettings);
  }, [isControlled, props.UserSettings]);

  useEffect(() => {
    if (isControlled) return;
    if (!contextUser?.UserID) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const result = await axios.get(`${endpoint}/${contextUser.UserID}`);
        if (cancelled) return;
        setUserSettings(result.data.userSettings);
        setUser(result.data.user);
      } catch (error) {
        if (!cancelled) {
          console.error('Passport fetch failed:', error);
          enqueueSnackbar('Failed to load passport information', { variant: 'error' });
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isControlled, contextUser?.UserID, enqueueSnackbar]);

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value);
    }
  }

  function handleCheckboxChange(input) {
    const target = input.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value ? true : false);
    }
  }

  // Store the picked date as an "MM/dd/yyyy" string (matches the DB column format)
  // rather than the raw Luxon DateTime, so saves write a valid short value.
  const fmtDate = (value) =>
    (value && DateTime.isDateTime(value) && value.isValid) ? value.toFormat('MM/dd/yyyy') : (value || null);

  const handleDOBChange = (value) => {
    const s = fmtDate(value);
    setUserSettings(prev => ({ ...prev, BP_P_DOB: s }));
    if (props.UseFormContext) {
      updateForm("UserSettings", "BP_P_DOB", s);
    }
  };

  function handleDateIssChange(value) {
    const s = fmtDate(value);
    setUserSettings(prev => ({ ...prev, BP_P_Date_Issued: s }));
    if (props.UseFormContext) {
      updateForm("UserSettings", "BP_P_Date_Issued", s);
    }
  }

  function handleDateExpChange(value) {
    const s = fmtDate(value);
    setUserSettings(prev => ({ ...prev, BP_P_Date_Exp: s }));
    if (props.UseFormContext) {
      updateForm("UserSettings", "BP_P_Date_Exp", s);
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(endpoint, { user: User, userSettings: UserSettings });
      enqueueSnackbar("Passport Section Saved!");
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Passport Section Save Failed: " + (ex.response?.data?.error || ex.message), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div id="tab8">
        <div className="row px-5 pt-5">
          <div className="col-md-12">
            <p>Please enter the following data exactly as it appears on your passport</p>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Passport Number<span className="required-field">*</span></label>
              <input type="text" maxLength="200" id='BP_P_Number' name='BP_P_Number' className="form-control" placeholder="Enter passport number" value={UserSettings.BP_P_Number || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Given Names (First, Middle)<span className="required-field">*</span></label>
              <input type="text" maxLength="200" id='BP_P_GivenName' name='BP_P_GivenName' className="form-control" placeholder="Enter given names" value={UserSettings.BP_P_GivenName || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Surname (Last Name)</label>
              <input type="text" maxLength="200" id='BP_P_Surname' name='BP_P_Surname' className="form-control" placeholder="Enter surname" value={UserSettings.BP_P_Surname || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Nationality</label>
              <input type="text" maxLength="200" id='BP_P_Nationality' name='BP_P_Nationality' className="form-control" placeholder="Enter nationality" value={UserSettings.BP_P_Nationality || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Date of Birth</label>
              <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
                
<DatePicker
    slotProps={{
        textField: {
            id: "BP_P_DOB",
        },
    }}
    value={toDT(UserSettings.BP_P_DOB)}
    onChange={handleDOBChange}
/>
              </LocalizationProvider>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">State Of Birth</label>
              <input type="text" id="BP_P_POB" maxLength="200" className="form-control" value={UserSettings.BP_P_POB || ''} placeholder="Enter state of birth" onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption" htmlFor="BP_P_Gender">Gender</label>
              <select name="BP_P_Gender" id="BP_P_Gender" className="DropDownField form-control" value={UserSettings.BP_P_Gender || '(select)'} onChange={handleFieldChange}>
                <option value="(select)">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Date of Issue</label>
              <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
                
<DatePicker
    slotProps={{
        textField: {
            id: "BP_P_Date_Issued",
        },
    }}
    value={toDT(UserSettings.BP_P_Date_Issued)}
    onChange={handleDateIssChange}
/>
              </LocalizationProvider>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Date of Expiration</label>
              <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
                
<DatePicker
    slotProps={{
        textField: {
            id: "BP_P_Date_Exp",
        },
    }}
    value={toDT(UserSettings.BP_P_Date_Exp)}
    onChange={handleDateExpChange}
/>
              </LocalizationProvider>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Profession</label>
              <input type="text" maxLength="200" id='BP_P_Profession' className="form-control" placeholder="Enter profession" value={UserSettings.BP_P_Profession || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Closest Airport</label>
              <input type="text" maxLength="200" id="BP_P_AirportName" className="form-control" placeholder="Enter closest airport" value={UserSettings.BP_P_AirportName || ''} onChange={handleFieldChange} />
            </div>
          </div>
          <hr />
          {!props.UseFormContext ?
            <div className="row my-5">
              <div className="col-md-12 text-center">
                <a className="button-default button-green" type='submit' onClick={handleSubmit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', minWidth: '120px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                  {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                  Save
                </a>
              </div>
            </div> : ""}
        </div>
      </div>
    </form>
  );
};
export default Passport;